import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { mediaUrl } from "../lib/media";
import { rupiah } from "../lib/format";
import { lencanaStatus } from "../hp/statusPesanan";
import { useChat } from "./chatKonteks";
import { sisipTanggal, jamWib, gabungPesan } from "./waktuChat";
import { IkonFoto, IkonKirim, IkonStruk } from "./IkonChat";
import { teksAman } from "../lib/galatAman";

const KUNCI_TOKEN = "hs_token";
const tokenSesi = () => { try { return localStorage.getItem(KUNCI_TOKEN) || ""; } catch { return ""; } };
const CATATAN = "Chat dan foto dihapus otomatis 7 hari sesudah pesan terakhir.";
const JEDA_MS = 5000;
const pesanGalat = (e, cadangan) => { const d = e && e.response && e.response.data && e.response.data.detail; return typeof d === "string" && d.trim() ? teksAman(d) : cadangan; };

function KartuKonteks({ k }) {
  if (!k) return null;
  if (k.jenis === "pesanan") {
    const [label] = lencanaStatus({ status: k.status, ongkir_pending: k.total === null });
    return (
      <div className="cht-konteks" data-testid="chat-konteks">
        <span className="cht-konteks__ikon"><IkonStruk /></span>
        <div><span>Tentang pesanan</span><b>{k.nomor_invoice}{k.total !== null ? ` · ${rupiah(k.total)}` : ""}</b><span>{label}{k.layanan ? ` · ${k.layanan}` : ""}</span></div>
      </div>
    );
  }
  return (
    <div className="cht-konteks" data-testid="chat-konteks">
      {k.gambar ? <img src={mediaUrl(k.gambar)} alt="" className="cht-konteks__foto" /> : <span className="cht-konteks__foto" />}
      <div><span>Tentang produk</span><b>{k.nama}</b><em>{rupiah(k.harga_per_pc)}/pc</em></div>
    </div>
  );
}

/** Ruang chat pembeli: dipakai halaman /chat (HP & komputer) dan panel komputer. Semua teks ditampilkan sebagai teks (React meng-escape; tautan tidak dibuat klik-able). */
export default function RuangChat({ konteks, kepala }) {
  const chat = useChat();
  const [pesan, setPesan] = useState([]);
  const [kartu, setKartu] = useState(null);
  const [isi, setIsi] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const [besar, setBesar] = useState(null);
  const [siap, setSiap] = useState(false);
  const [param, setParam] = useState(konteks || null);
  const akhir = useRef(0);
  const sesi = useRef(tokenSesi());   // token yang membuka ruang ini; akun lain masuk di tab lain → riwayat tidak boleh tercampur
  const bawah = useRef(null);
  const unggah = useRef(null);
  useEffect(() => { setParam(konteks || null); }, [konteks]);
  const { muatStatus } = chat;

  const sesiBerganti = useCallback(() => {
    if (tokenSesi() === sesi.current) return false;
    setPesan([]); setKartu(null); setBesar(null); setIsi("");
    window.location.reload();
    return true;
  }, []);
  const gabung = useCallback((baru, dibacaSampai) => setPesan((lama) => gabungPesan(lama, baru, dibacaSampai)), []);
  // Kursor hanya maju dari hasil baca server, bukan dari respons kirim: balasan yang datang tepat sebelum pesan kita tidak terlewat
  const majukan = (baru) => { if (baru.length) akhir.current = Math.max(akhir.current, ...baru.map((p) => p.id)); };

  const muatSemua = useCallback(async () => {
    if (sesiBerganti()) return;
    const { data } = await api.get("/api/chat/percakapan", { params: { tandai: 1 } });
    if (sesiBerganti()) return;
    akhir.current = 0;
    setPesan([]);
    gabung(data.pesan, data.dibaca_sampai);
    majukan(data.pesan);
    setSiap(true);
    if (muatStatus) muatStatus();
  }, [gabung, muatStatus, sesiBerganti]);

  useEffect(() => { muatSemua().catch(() => setSiap(true)); }, [muatSemua]);
  useEffect(() => {
    api.get("/api/chat/konteks", { params: param || {} }).then(({ data }) => setKartu(data.konteks || null))
      .catch(() => { setKartu(null); setParam(null); });   // bukan milik pembeli ini / produk tidak aktif → percakapan umum
  }, [param]);
  // Polling: hanya selama tab terlihat
  useEffect(() => {
    const t = setInterval(() => {
      if (document.hidden || sesiBerganti()) return;
      api.get("/api/chat/percakapan", { params: { tandai: 1, setelah: akhir.current || undefined } })
        .then(({ data }) => {
          if (sesiBerganti()) return;
          gabung(data.pesan, data.dibaca_sampai); majukan(data.pesan);
          if (muatStatus && data.pesan.some((p) => p.pengirim === "admin")) muatStatus();   // lencana di kepala ikut terbaru
        }).catch(() => {});
    }, JEDA_MS);
    const pantau = (e) => { if (!e.key || e.key === KUNCI_TOKEN) sesiBerganti(); };
    window.addEventListener("storage", pantau);
    return () => { clearInterval(t); window.removeEventListener("storage", pantau); };
  }, [gabung, sesiBerganti, muatStatus]);
  useEffect(() => { if (bawah.current && bawah.current.scrollIntoView) bawah.current.scrollIntoView({ block: "end" }); }, [pesan.length]);

  const kirimTeks = async (e) => {
    e.preventDefault();
    const t = isi.trim();
    if (!t || sibuk || sesiBerganti()) return;
    setSibuk(true);
    try {
      const { data } = await api.post("/api/chat/pesan", { teks: t, ...(param || {}) });
      if (sesiBerganti()) return;
      gabung([data]);
      setIsi("");
    } catch (er) { toast.error(pesanGalat(er, "Pesan gagal dikirim. Coba lagi.")); }
    setSibuk(false);
  };
  const kirimFoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f || sibuk || sesiBerganti()) return;
    setSibuk(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/api/chat/foto", form, { params: param || {} });
      if (sesiBerganti()) return;
      gabung([data]);
    } catch (er) { toast.error(pesanGalat(er, "Foto gagal dikirim. Coba lagi.")); }
    setSibuk(false);
  };

  return (
    <>
      {kepala}
      <KartuKonteks k={kartu} />
      <main className="cht-isi" role="log" aria-live="polite" aria-label="Isi chat" data-testid="chat-isi">
        {siap && !pesan.length && <p className="cht-kosong" data-testid="chat-kosong">Tulis pertanyaan Anda. Kami membalas di sini.</p>}
        {sisipTanggal(pesan).map((p) => (p.pemisah
          ? <span key={p.kunci} className="cht-tanggal">{p.pemisah}</span>
          : (
            <div key={p.id} className={`cht-baris ${p.pengirim === "pembeli" ? "is-saya" : "is-toko"}`} data-testid={`chat-pesan-${p.id}`}>
              {p.foto
                ? <button type="button" className="cht-foto" onClick={() => setBesar(p)} aria-label="Lihat foto lebih besar"><img src={p.foto.thumb} alt="Foto dalam chat" onError={() => muatSemua().catch(() => {})} /></button>
                : <span className="cht-gelembung">{p.teks}</span>}
              <span className="cht-jam">{jamWib(p.dibuat_pada)}{p.pengirim === "pembeli" && p.dibaca ? " · Dibaca" : ""}</span>
            </div>
          )))}
        <div ref={bawah} />
      </main>
      <p className="cht-catatan">{CATATAN}</p>
      <form className="cht-tulis" onSubmit={kirimTeks}>
        <input ref={unggah} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={kirimFoto} data-testid="chat-input-foto" />
        <button type="button" className="cht-tulis__foto" aria-label="Kirim foto" disabled={sibuk} onClick={() => unggah.current && unggah.current.click()} data-testid="chat-tombol-foto"><IkonFoto /></button>
        <label><span className="cht-sr">Tulis pesan</span>
          <input type="text" value={isi} maxLength={1000} placeholder="Tulis pesan…" onChange={(e) => setIsi(e.target.value)} data-testid="chat-input" autoComplete="off" />
        </label>
        <button type="submit" className="cht-tulis__kirim" aria-label="Kirim" disabled={sibuk || !isi.trim()} data-testid="chat-kirim"><IkonKirim /></button>
      </form>
      {besar && (
        <div className="cht-besar" role="dialog" aria-modal="true" aria-label="Foto" onClick={() => setBesar(null)} data-testid="chat-foto-besar">
          <img src={besar.foto.url} alt="Foto dalam chat" />
        </div>
      )}
    </>
  );
}
